import { useTranslation } from 'react-i18next'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { useTheme } from '@mui/material/styles'
import {
  formatMinutes,
  maintenanceBarLayout,
  type MaintenanceSchedule,
  type MaintenanceStatus,
  type MaintenanceZone,
} from '@jupi/shared'
import { formatDate } from '../format'

// mik-ng's maintenance bar -- red at the left for the hours kept back for the
// flight to maintenance, amber for the tolerance past the due point, green for
// the cycle -- in its bright colours. As hours are flown the green is used up
// from the right and replaced by grey, so the colour that is left says where
// the aircraft is; a small pointer under the bar rides on the level, and the
// hours a member may still plan to fly head it.

const COLOURS = {
  red: '#dd5235',
  amber: '#f9de55',
  green: '#66cc66',
} as const

const ZONE_COLOUR: Record<MaintenanceZone, string> = {
  green: 'success.main',
  amber: 'warning.main',
  red: 'error.main',
  over: 'error.main',
}

export function MaintenanceBar({
  schedule,
  status,
  compact = false,
}: {
  schedule: Pick<MaintenanceSchedule, 'cycleHours' | 'toleranceHours' | 'reservedHours' | 'nextType' | 'nextDueMinutes' | 'nextDueDate'>
  status: MaintenanceStatus
  compact?: boolean
}) {
  const { t } = useTranslation()
  const theme = useTheme()
  const layout = maintenanceBarLayout(schedule, status.minutesToDue)
  const headline =
    status.zone === 'over'
      ? t('maintenance.bar.over')
      : status.zone === 'red'
        ? t('maintenance.bar.reserved')
        : t('maintenance.bar.usable', { time: formatMinutes(status.usableMinutes) })
  const grey = theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.grey[300]
  const level = layout.marker

  return (
    <Box>
      <Typography variant="body2" sx={{ fontWeight: 500, color: ZONE_COLOUR[status.zone] }}>
        {headline}
      </Typography>
      <Box role="img" aria-label={headline} sx={{ mt: 1, mb: compact ? 0.5 : 1 }}>
        <Box
          sx={{
            position: 'relative',
            display: 'flex',
            height: 10,
            borderRadius: 5,
            overflow: 'hidden',
            bgcolor: grey,
          }}
        >
          {(['red', 'amber', 'green'] as const).map((zone) =>
            layout[zone] > 0 ? <Box key={zone} sx={{ width: `${layout[zone]}%`, bgcolor: COLOURS[zone] }} /> : null,
          )}
          {/* Used-up hours: grey from the right edge back to the level. */}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              bottom: 0,
              right: 0,
              width: `${100 - level}%`,
              bgcolor: grey,
              transition: 'width 1s',
            }}
          />
        </Box>
        {/* The pointer, under the bar, on the level. */}
        <Box sx={{ position: 'relative', height: 8 }}>
          <Box
            sx={{
              position: 'absolute',
              top: 1,
              left: `clamp(0px, calc(${level}% - 5px), calc(100% - 10px))`,
              width: 10,
              height: 7,
              transition: 'left 1s',
              bgcolor: 'text.secondary',
              clipPath: 'polygon(50% 0, 0 100%, 100% 100%)',
            }}
          />
        </Box>
      </Box>
      <Stack direction="row" sx={{ justifyContent: 'space-between', flexWrap: 'wrap', columnGap: 1 }}>
        <Typography variant="caption" color="text.secondary">
          {t('maintenance.bar.next', { type: schedule.nextType, at: formatMinutes(schedule.nextDueMinutes) })}
        </Typography>
        {schedule.nextDueDate ? (
          <Typography variant="caption" color={status.daysToDue !== null && status.daysToDue < 0 ? 'error' : 'text.secondary'}>
            {t('maintenance.bar.byDate', { date: formatDate(`${schedule.nextDueDate}T12:00:00Z`) })}
          </Typography>
        ) : null}
      </Stack>
    </Box>
  )
}
